import { apiClient } from '@/shared/lib/apiClient';
import type {
  SubscriptionResponseDTO,
  SubscriptionListQuery,
  SubscriptionListResponse,
  CreateSubscriptionDTO,
  CancelSubscriptionDTO,
  ChangePlanDTO,
  SubscriptionEvent,
  SsoHandoffResponse,
  CheckoutSessionCurrentDTO,
} from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export async function listSubscriptions(
  query?: SubscriptionListQuery,
): Promise<SubscriptionListResponse> {
  const params: Record<string, string> = {};
  if (query?.cursor) params['cursor'] = query.cursor;
  if (query?.limit !== undefined) params['limit'] = String(query.limit);
  if (query?.status) params['status'] = query.status;
  if (query?.product_id) params['product_id'] = query.product_id;
  if (query?.tenant_id) params['tenant_id'] = query.tenant_id;

  const res = await apiClient.get<ApiResponse<SubscriptionListResponse>>('/api/v1/subscriptions', {
    params,
  });
  return res.data;
}

export async function getSubscription(id: string): Promise<SubscriptionResponseDTO> {
  const res = await apiClient.get<ApiResponse<SubscriptionResponseDTO>>(
    `/api/v1/subscriptions/${id}`,
  );
  return res.data;
}

export async function createSubscription(
  data: CreateSubscriptionDTO,
): Promise<SubscriptionResponseDTO> {
  const res = await apiClient.post<ApiResponse<SubscriptionResponseDTO>>(
    '/api/v1/subscriptions',
    data,
  );
  return res.data;
}

export async function getCurrentCheckoutSession(
  checkoutSession: string,
): Promise<CheckoutSessionCurrentDTO | null> {
  const res = await apiClient.get<ApiResponse<CheckoutSessionCurrentDTO | null>>(
    '/api/v1/auth/checkout-session/current',
    { params: { checkout_session: checkoutSession } },
  );
  return res.data;
}

export async function cancelSubscription(
  id: string,
  data?: CancelSubscriptionDTO,
): Promise<SubscriptionResponseDTO> {
  const res = await apiClient.post<ApiResponse<SubscriptionResponseDTO>>(
    `/api/v1/subscriptions/${id}/cancel`,
    data ?? {},
  );
  return res.data;
}

export async function suspendSubscription(id: string): Promise<SubscriptionResponseDTO> {
  const res = await apiClient.post<ApiResponse<SubscriptionResponseDTO>>(
    `/api/v1/subscriptions/${id}/suspend`,
    {},
  );
  return res.data;
}

export async function resumeSubscription(id: string): Promise<SubscriptionResponseDTO> {
  const res = await apiClient.post<ApiResponse<SubscriptionResponseDTO>>(
    `/api/v1/subscriptions/${id}/resume`,
    {},
  );
  return res.data;
}

export async function changePlan(
  id: string,
  data: ChangePlanDTO,
): Promise<SubscriptionResponseDTO> {
  const res = await apiClient.post<ApiResponse<SubscriptionResponseDTO>>(
    `/api/v1/subscriptions/${id}/change-plan`,
    data,
  );
  return res.data;
}

export async function getSubscriptionEvents(id: string): Promise<SubscriptionEvent[]> {
  const res = await apiClient.get<ApiResponse<SubscriptionEvent[]>>(
    `/api/v1/subscriptions/${id}/events`,
  );
  return res.data;
}

export async function ssoHandoff(id: string): Promise<SsoHandoffResponse> {
  const res = await apiClient.post<ApiResponse<SsoHandoffResponse>>(
    `/api/v1/subscriptions/${id}/sso-handoff`,
    {},
  );
  return res.data;
}
